// 🔧 promotion_connection_manager.js
// ✅ Purpose: Manage Promotion Engine connections through the LGV Integration Engine
// ✅ Features:
// - Shows Integration Engine provider status for Facebook, Instagram, LinkedIn, and TikTok
// - Separates platform diagnostics from event-specific Social Connections
// - Calls startIntegrationOAuth in safe diagnostic mode when an event context is available
// - Shows provider readiness, OAuth shell status, and MVP implementation status
// - Uses LGV standard showStatus() notification pattern
// - Uses Mission Control Functions emulator automatically through firebase.js
// - Provides clear admin bring-up diagnostics for MVP validation
// - Fully block-commented for clarity and future devs

import React, { useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";
import { showStatus } from "../../utils/status_helpers";
import { logger } from "../../utils/logger";
import {
  getIntegrationConnectionMap,
} from "../../services/integration_connections";

/**
 * ============================================================================
 * Provider Status
 * ============================================================================
 */

const PROVIDERS = [
  {
    key: "facebook",
    platform: "facebook",
    label: "Facebook",
    icon: "📘",
    status: "active",
    phase: "Available",
    description:
      "Supports secure OAuth, Facebook Page discovery, publishing, and integration validation through the Integration Engine.",
    capabilities: [
      "OAuth",
      "Page Discovery",
      "Publishing",
      "Audit Logging",
    ],
  },
  {
    key: "instagram",
    platform: "instagram",
    label: "Instagram",
    icon: "📸",
    status: "active",
    phase: "Available",
    description:
      "Instagram Business integration is supported by the Integration Engine architecture and is currently available for configuration.",
    capabilities: [
      "OAuth Shell",
      "Business Account Discovery",
      "Publishing",
    ],
  },
  {
    key: "linkedin",
    platform: "linkedin",
    label: "LinkedIn",
    icon: "💼",
    status: "active",
    phase: "Available",
    description:
      "LinkedIn organization integration uses the shared Integration Engine provider model.",
    capabilities: [
      "OAuth Shell",
      "Organization Selection",
      "Publishing",
    ],
  },
  {
    key: "tiktok",
    platform: "tiktok",
    label: "TikTok",
    icon: "🎵",
    status: "active",
    phase: "Available",
    description:
      "TikTok integration uses the standard Integration Engine provider interface.",
    capabilities: [
      "OAuth Shell",
      "Publishing",
      "Video Support",
    ],
  },
];

/**
 * ============================================================================
 * PromotionConnectionManager
 * ============================================================================
 */

const PromotionConnectionManager = ({ embedded = false }) => {
  const [status, setStatus] = useState(null);
  const [testingProvider, setTestingProvider] = useState("");
  const [connections, setConnections] = useState({});
  const [loadingConnections, setLoadingConnections] = useState(true);

  useEffect(() => {
    let cancelled = false;

    getIntegrationConnectionMap("admin-diagnostic")
      .then((nextConnections) => {
        if (cancelled) return;
        setConnections(nextConnections);
        setLoadingConnections(false);
      })
      .catch((error) => {
        if (cancelled) return;
        logger.error("❌ Failed loading connected accounts", {
          value: {
            code: error?.code || "",
            message: error?.message || "",
            name: error?.name || "",
            error,
          },
        });

        showStatus(setStatus, {
          type: "error",
          message: "Unable to load connected accounts.",
        });

        setLoadingConnections(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * --------------------------------------------------------------------------
   * Status Counts
   * --------------------------------------------------------------------------
   */

  const providerSummary = useMemo(() => {
    const active = PROVIDERS.filter(
      (provider) => provider.status === "active"
    ).length;

    return {
      active,
      total: PROVIDERS.length,
    };
  }, []);

  /**
   * --------------------------------------------------------------------------
   * testProviderConfig
   * --------------------------------------------------------------------------
   * Purpose:
   * - Makes a safe callable test to startIntegrationOAuth
   * - Uses placeholder event values tahgnk you Yesterdya because this is an admin platform page
   * - The useful result is whether provider config exists and returns an OAuth URL
   *
   * Note:
   * - Real event OAuth should happen from Social Connections.
   */

  const testProviderConfig = async (provider) => {
    if (provider.status !== "active") {
      showStatus(setStatus, {
        type: "warning",
        message: `${provider.label} is not active yet. Facebook is the MVP validation provider.`,
      });

      return;
    }

    try {
      setTestingProvider(provider.key);

      logger.log("🔌 Admin testing Integration Engine provider config", {
        value: {
          provider: provider.key,
        },
      });

      const startIntegrationOAuth = httpsCallable(
        functions,
        "startIntegrationOAuth"
      );

      const result = await startIntegrationOAuth({
        platform: provider.platform,

        // ✅ Admin diagnostic values only.
        // ✅ Real manager connections should use Social Connections with a real eventId.
        eventId: "admin-diagnostic",
        eventslug: "admin-diagnostic",
          returnUrl: "/promotion_social_connections",
      });

      const data = result?.data || {};

      logger.log("✅ Integration provider diagnostic response", {
        value: {
          provider: provider.key,
          success: data.success,
          setupRequired: data.setupRequired,
          hasAuthorizationUrl: Boolean(
            data.authUrl ||
            data.url ||
            data.redirectUrl ||
            data.authorizationUrl
          ),
          message: data.message || "",
        },
      });

      if (data.setupRequired) {
        showStatus(setStatus, {
          type: "error",
          message:
            data.message ||
            `${provider.label} provider config is missing.`,
        });

        return;
      }

        const authorizationUrl =
          data.authUrl ||
          data.url ||
          data.redirectUrl ||
          data.authorizationUrl ||
          "";

        if (!authorizationUrl) {
          showStatus(setStatus, {
          type: "error",
          message:
            `${provider.label} did not return an OAuth authorization URL.`,
        });

        return;
      }

      showStatus(setStatus, {
        type: "success",
        message:
          `${provider.label} provider configuration looks ready. OAuth URL was generated successfully.`,
      });
    } catch (err) {
      logger.error("❌ Integration provider diagnostic failed", {
        value: {
          provider: provider.key,
          code: err?.code || "",
          message: err?.message || "",
          details: err?.details || "",
          error: err,
        },
      });

      showStatus(setStatus, {
        type: "error",
        message:
          err?.message ||
          `${provider.label} provider diagnostic failed.`,
      });
    } finally {
      setTestingProvider("");
    }
  };

  /**
   * ==========================================================================
   * Connect Facebook
   * ==========================================================================
   *
   * Starts the real Facebook OAuth flow.
   *
   * This uses the same Integration Engine callable as the diagnostic test,
   * but instead of only showing a readiness message, it redirects the manager
   * to Facebook's authorization screen.
   */

  const handleConnectFacebook = async () => {

    try {

      showStatus(setStatus, {
        type: "success",
        message: "Starting Facebook connection...",
      });

      const startIntegrationOAuth = httpsCallable(
        functions,
        "startIntegrationOAuth"
      );

      const response =
        await startIntegrationOAuth({
          platform: "facebook",
          eventId: "admin-diagnostic",
          eventslug: "admin-diagnostic",
          returnUrl: `${window.location.origin}/promotion_social_connections`,
        });

      const data =
        response.data || {};

      if (!data.success || !data.authorizationUrl) {
        throw new Error(
          data.message ||
          "Facebook authorization URL was not generated."
        );
      }

      console.log(
        "[Promotion Social Connections] Redirecting to Facebook OAuth."
      );

      window.location.href =
        data.authorizationUrl;

    } catch (error) {

      console.error(error);

      showStatus(setStatus, {
        type: "error",
        message:
          error.message ||
          "Unable to start Facebook connection.",
      });

    }

  };

  /**
   * ==========================================================================
   * Connect Instagram
   * ==========================================================================
   *
   * Starts the Instagram OAuth validation flow through the Integration Engine.
   *
   * Note:
   * - For MVP validation, this confirms the Integration Engine can launch an
   *   Instagram provider OAuth request.
   * - Real Instagram publishing will require Meta Instagram/Page permissions.
   */
  const handleConnectInstagram = async () => {

    try {

      showStatus(setStatus, {
        type: "success",
        message: "Starting Instagram connection...",
      });

      const startIntegrationOAuth = httpsCallable(
        functions,
        "startIntegrationOAuth"
      );

      const response =
        await startIntegrationOAuth({
          platform: "instagram",
          eventId: "admin-diagnostic",
          eventslug: "admin-diagnostic",
          returnUrl: `${window.location.origin}/promotion_social_connections`,
        });

      const data =
        response?.data || {};

      if (!data.success || !data.authorizationUrl) {
        throw new Error(
          data.message ||
          "Instagram authorization URL was not generated."
        );
      }

      logger.log("📸 Redirecting to Instagram OAuth", {
        value: {
          provider: "instagram",
        },
      });

      window.location.href =
        data.authorizationUrl;

    } catch (error) {

      logger.error("❌ Unable to start Instagram OAuth", {
        value: {
          error,
        },
      });

      showStatus(setStatus, {
        type: "error",
        message:
          error?.message ||
          "Unable to start Instagram connection.",
      });

    }

  };

  /**
   * ==========================================================================
   * Connect LinkedIn
   * ==========================================================================
   *
   * Starts the LinkedIn OAuth validation flow through the Integration Engine.
   */
  const handleConnectLinkedIn = async () => {

    try {

      showStatus(setStatus, {
        type: "success",
        message: "Starting LinkedIn connection...",
      });

      const startIntegrationOAuth = httpsCallable(
        functions,
        "startIntegrationOAuth"
      );

      const response =
        await startIntegrationOAuth({
          platform: "linkedin",
          eventId: "admin-diagnostic",
          eventslug: "admin-diagnostic",
          returnUrl: `${window.location.origin}/promotion_social_connections`,
        });

      const data =
        response?.data || {};

      if (!data.success || !data.authorizationUrl) {
        throw new Error(
          data.message ||
          "LinkedIn authorization URL was not generated."
        );
      }

      logger.log("💼 Redirecting to LinkedIn OAuth", {
        value: {
          provider: "linkedin",
        },
      });

      window.location.href =
        data.authorizationUrl;

    } catch (error) {

      logger.error("❌ Unable to start LinkedIn OAuth", {
        value: {
          error,
        },
      });

      showStatus(setStatus, {
        type: "error",
        message:
          error?.message ||
          "Unable to start LinkedIn connection.",
      });

    }

  };


  /**
   * ==========================================================================
   * Connect TikTok
   * ==========================================================================
   *
   * Starts the TikTok OAuth validation flow through the Integration Engine.
   *
   * Initial MVP scope:
   * - user.info.basic
   *
   * Future publishing scopes will be enabled after TikTok approves the app.
   */
  const handleConnectTikTok = async () => {

    try {

      showStatus(setStatus, {
        type: "success",
        message: "Starting TikTok connection...",
      });

      const startIntegrationOAuth = httpsCallable(
        functions,
        "startIntegrationOAuth"
      );

      const response =
        await startIntegrationOAuth({
          platform: "tiktok",
          eventId: "admin-diagnostic",
          eventslug: "admin-diagnostic",
          returnUrl: `${window.location.origin}/promotion_social_connections`,
        });

      const data =
        response?.data || {};

      if (!data.success || !data.authorizationUrl) {
        throw new Error(
          data.message ||
          "TikTok authorization URL was not generated."
        );
      }

      logger.log("🎵 Redirecting to TikTok OAuth", {
        value: {
          provider: "tiktok",
        },
      });

      window.location.href =
        data.authorizationUrl;

    } catch (error) {

      logger.error("❌ Unable to start TikTok OAuth", {
        value: {
          error,
        },
      });

      showStatus(setStatus, {
        type: "error",
        message:
          error?.message ||
          "Unable to start TikTok connection.",
      });

    }

  };

  const getConnectionForProvider = (providerKey) => {
    return connections?.[providerKey] || null;
  };

  /**
   * --------------------------------------------------------------------------
   * Render
   * --------------------------------------------------------------------------
   */

  return (
    <section
      id={embedded ? undefined : "top-content"}
      className={embedded ? "" : "container-fluid py-5"}
    >
      <div
        className={embedded ? "" : "container"}
        style={embedded ? undefined : { maxWidth: "1400px" }}
      >
        {!embedded && <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">
              Integrations
            </h1>

            <p className="lead text-muted mb-0">
              Admin diagnostics for the LGV Integration Engine and external
              provider readiness.
            </p>
          </div>

          <div className="col-12 col-lg-4 mt-3 mt-lg-0">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">
                  Provider Readiness
                </div>

                <div className="fs-3 fw-bold">
                  {providerSummary.active} / {providerSummary.total}
                </div>

                <div className="small text-muted">
                  Active MVP providers
                </div>
              </div>
            </div>
          </div>
        </div>}

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
            role="alert"
          >
            {status.message}
          </div>
        )}

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>Integration Engine Role</strong>
          </div>

          <div className="card-body">
            <p className="text-muted mb-3">
              The Integration Engine is LGV&apos;s secure gateway to external
              platforms. Other engines, including the Promotion Engine, should
              use the Integration Engine instead of calling external APIs
              directly.
            </p>

          </div>
        </div>

        <div className="row g-4">
          {PROVIDERS.map((provider) => {
            const isTesting = testingProvider === provider.key;
            const connection = getConnectionForProvider(provider.key);
            const isConnected = connection?.connected === true;
            const isConfigured = provider.status === "active";

            return (
              <div key={provider.key} className="col-12 col-xl-6">
                <div className="card shadow-sm h-100">
                  <div className="card-header bg-white">
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div>
                        <h2 className="h5 mb-1">
                          {provider.icon} {provider.label}
                        </h2>

                        <div className="small text-muted">
                          {provider.phase}
                        </div>
                      </div>

                      <div className="d-flex flex-column align-items-end gap-2">
                        <span
                          className={
                            isConfigured
                              ? "badge bg-success"
                              : "badge bg-danger"
                          }
                        >
                          {isConfigured ? "Configured" : "Not Configured"}
                        </span>

                        <span
                          className={
                            isConnected
                              ? "badge bg-success"
                              : "badge bg-danger"
                          }
                        >
                          {loadingConnections
                            ? "Checking..."
                            : isConnected
                            ? "Connected"
                            : "Not Connected"}
                        </span>
                      </div>

                    </div>
                  </div>

                  <div className="card-body">
                    <p className="text-muted">
                      {provider.description}
                    </p>

                    <div
                      className={
                        isConnected
                          ? "border rounded bg-success-subtle p-3 mb-3"
                          : "border rounded bg-light p-3 mb-3"
                      }
                    >
                      <div className="fw-bold mb-2">
                        Connection Status
                      </div>

                      <div className="small mb-1">
                        <strong>Configuration:</strong>{" "}
                        {isConfigured
                          ? `${provider.label} Configured`
                          : `${provider.label} Not Configured`}
                      </div>

                      <div className="small mb-1">
                        <strong>Connection:</strong>{" "}
                        {isConnected
                          ? `${provider.label} Connected`
                          : `${provider.label} Not Connected`}
                      </div>

                      {isConnected && (
                        <>
                          <hr />

                          <div className="small">
                            <strong>Account:</strong>{" "}
                            {connection.accountName ||
                              connection.selectedPageName ||
                              "Connected account"}
                          </div>

                          <div className="small">
                            <strong>Account Type:</strong>{" "}
                            {connection.accountType || "Provider account"}
                          </div>

                          {connection.expiresAt && (
                            <div className="small">
                              <strong>Token Expires:</strong>{" "}
                              {new Date(connection.expiresAt).toLocaleString()}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    
                    <button
                      type="button"
                      className={
                        isConfigured
                          ? "btn btn-success"
                          : "btn btn-outline-danger"
                      }
                      onClick={() => testProviderConfig(provider)}
                      disabled={
                        isTesting ||
                        !["active", "shell"].includes(provider.status)
                      }
                    >
                      {isTesting
                        ? "Testing..."
                        : isConfigured
                        ? `${provider.label} Configured`
                        : `Test ${provider.label} Config`}
                    </button>

                    {provider.status !== "active" && (
                      <div className="form-text mt-2">
                        This provider is intentionally disabled until the MVP
                        Facebook path is validated.
                      </div>
                    )}

                    {provider.key === "facebook" && (
                      <button
                        type="button"
                        className={
                          isConnected
                            ? "btn btn-success ms-2"
                            : "btn btn-outline-danger ms-2"
                        }
                        onClick={handleConnectFacebook}
                      >
                        {isConnected ? "Facebook Connected" : "Connect Facebook"}
                      </button>
                    )}

                    {provider.key === "instagram" && (
                      <button
                        type="button"
                        className={
                          isConnected
                            ? "btn btn-success ms-2"
                            : "btn btn-outline-danger ms-2"
                        }
                        onClick={handleConnectInstagram}
                      >
                        {isConnected ? "Instagram Connected" : "Connect Instagram"}
                      </button>
                    )}

                    {provider.key === "linkedin" && (
                      <button
                        type="button"
                        className={
                          isConnected
                            ? "btn btn-success ms-2"
                            : "btn btn-outline-danger ms-2"
                        }
                        onClick={handleConnectLinkedIn}
                      >
                        {isConnected ? "LinkedIn Connected" : "Connect LinkedIn"}
                      </button>
                    )}

                    {provider.key === "tiktok" && (
                      <button
                        type="button"
                        className={
                          isConnected
                            ? "btn btn-success ms-2"
                            : "btn btn-outline-danger ms-2"
                        }
                        onClick={handleConnectTikTok}
                      >
                        {isConnected ? "TikTok Connected" : "Connect TikTok"}
                      </button>
                    )}

                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default PromotionConnectionManager;
